import { css, cx } from '@linaria/core'
import type { FC } from 'react'
import { useAppSelector } from '#app/store'
import type { RecoverableConfiguration, TruckConfiguration } from '#app/store/slices/configurationSlice'
import { Icon } from '#components/Icon'
import { Spinner } from '#components/Spinner'
import FileDataIcon from '#icons/file-data-24.svg'
import KeyIcon from '#icons/key-24.svg'
import LockIcon from '#icons/lock-24.svg'
import { style } from '#styles/index'
import { testIds } from '#utils/testIds'
import { configurationLeadingType, configurationStatus } from './configurationStatus'

export type ConfigurationStatus = ReturnType<typeof configurationStatus>

const statusIconClassName = css`
  position: relative;
  color: ${style.theme.textDefault};

  &.active {
    color: ${style.theme.textAccent};
  }

  > .icon {
    position: absolute;
    transition: 0.25s ease top, 0.25s ease bottom, 0.25s ease left, 0.25s ease right, 0.25s ease width, 0.25s ease height;

    top: 1px;
    bottom: 1px;
    left: 1px;
    right: 1px;

    &.saving {
      top: 3px;
      bottom: 3px;
      left: 3px;
      right: 3px;
    }
  }

  > .spinner {
    transition: 0.25s ease opacity;
    
    opacity: 0;
    &.saving {
      opacity: 1;
    }
  }

  > .dot {
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 3px;
    height: 3px;
    border-radius: 3px;
    background-color: ${style.theme.textAccent};
    transition: 0.25s ease opacity;
    
    opacity: 0;
    &.unsaved {
      opacity: 1;
    }
  }
`
export const StatusIcon: FC<{
  active: boolean
  state: 'unsaved' | 'saving' | 'saved'
  type: 'leading' | 'alternative' | 'read-only' | 'none'
}> = ({ active, state, type }) => {
  const icon = (() => {
    switch (type) {
      case 'leading':
        return KeyIcon
      case 'alternative':
        return FileDataIcon
      case 'read-only':
        return LockIcon
      case 'none':
        return undefined
    }
  })()

  const className = cx(statusIconClassName, active && 'active')
  return (
    <div className={className} data-type={type} data-state={state} data-testid={testIds.header.statusIcon}>
      <Spinner
        className={cx('spinner', state === 'saving' && 'saving')}
        size="xs"
        trailColor={active ? style.theme.textAccent : style.theme.textDefault}
      />
      <Icon iconUrl={icon} size={state === 'saving' ? 'xxs' : 14} className={cx(state === 'saving' && 'saving')} />
      <div className={cx('dot', state === 'unsaved' && 'unsaved')} />
    </div>
  )
}

export const ConfigurationStatusIcon: FC<{
  configuration: TruckConfiguration | RecoverableConfiguration
  className?: string
  isActive?: boolean
}> = ({ configuration, isActive }) => {
  const leadingConfiguration = useAppSelector((x) => x.truckConfigurations.leadingConfiguration)
  const type = configurationLeadingType(configuration, leadingConfiguration)
  return <StatusIcon active={isActive ?? false} type={type} state={configurationStatus(configuration)} />
}
